import { createFileRoute, Link } from "@tanstack/react-router";
import { Crosshair, Radar, Rocket, Shield, Lightbulb, Building2, ArrowRight } from "lucide-react";
import hero from "@/assets/public/hero-lockheed.jpg";
import heroVideo from "@/assets/public/hero-video.mp4.asset.json";
import energyImg from "@/assets/public/story-energy.jpg";
import mfgImg from "@/assets/public/story-manufacturing.jpg";
import autoImg from "@/assets/public/story-autonomy.jpg";
import foundersImg from "@/assets/public/story-founders.jpg";
import { Framework, SdvosbBadge } from "@/components/public/framework";
import chart from "@/assets/public/gulf-chart.jpg";
import { Eyebrow, Shell } from "@/components/public/site";
import { useSiteCopy } from "@/hooks/useSiteCopy";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Maven Scouts — Bold. Vigilant. Innovative." },
      { name: "description", content: "Commercializing dual-use technologies for defense and critical infrastructure through acceleration, OSINT market intelligence, and rapid prototyping." },
      { property: "og:title", content: "Maven Scouts — Bold. Vigilant. Innovative." },
      { property: "og:description", content: "Commercializing dual-use technologies for defense and critical infrastructure." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Index,
});

const pillars = [
  { icon: Rocket, badge: "Pillar 01", title: "Dual-Use Acceleration", body: "Transitioning commercial innovation to defense needs — from first engagement to mission adoption." },
  { icon: Radar, badge: "Pillar 02", title: "Market Intelligence & OSINT", body: "Verifiable, multi-source intelligence across the 16 critical infrastructure sectors." },
  { icon: Crosshair, badge: "Pillar 03", title: "Rapid Prototyping & Edge Deployment", body: "Evaluating, testing, and deploying mission capabilities on accelerated timelines." },
];

const audiences = [
  { icon: Shield, title: "Defense & Government Agencies", body: "Rapidly discover and field vetted commercial technology." },
  { icon: Lightbulb, title: "Dual-Use Founders & Startups", body: "Acceleration, SBIR/STTR navigation, and DoD mission transition." },
  { icon: Building2, title: "Industry Primes & Capital Partners", body: "A qualified technology pipeline and strategic integration." },
];

const spotlights = [
  { name: "ATTRAP", full: "Advanced Tactical Tracking for Resilient Autonomous Platforms", specs: [["Domain", "Air / Ground / Maritime"], ["Navigation", "GPS-denied resilient"], ["Compute", "Edge / onboard"]], uses: ["Persistent ISR tracking", "Counter-UAS cueing", "Commercial asset tracking"] },
  { name: "REMI", full: "Dual-use sensing & intelligence platform", specs: [["Mode", "Dual-use"], ["Inputs", "Multi-sensor fusion"], ["Form factor", "Rapidly deployable"]], uses: ["Perimeter & base security", "Critical infrastructure monitoring", "Disaster response"] },
];

const story = [
  { img: energyImg, step: "Understand", title: "Energy Systems", alt: "Engineers inspecting Gulf Coast LNG facility at dusk" },
  { img: mfgImg, step: "Build", title: "Advanced Manufacturing", alt: "Engineer operating CNC machine and robotic arm" },
  { img: autoImg, step: "Grow", title: "Autonomy & Maritime", alt: "Team testing an autonomous vessel on a Louisiana bayou" },
  { img: foundersImg, step: "Scale", title: "Founders & Capital", alt: "Founder pitching to investors at a pitch competition" },
];

const stats = [
  ["20+", "Years military & defense leadership"],
  ["16", "Critical infrastructure sectors covered"],
  ["Gulf", "Energy & maritime corridor focus"],
  ["Rapid", "Deployment timelines"],
];

function Index() {
  const { getCopy } = useSiteCopy();

  return (
    <Shell>
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy text-navy-foreground">
        <img src={hero} alt="Louisiana Gulf coast at dusk: fighter jet and drone overhead, port cranes, LNG tanker, offshore rig, and engineers with a prototype aircraft" width={1920} height={1088} className="absolute inset-0 h-full w-full object-cover object-[75%_center]" />
        <video src={heroVideo.url} poster={hero} autoPlay muted loop playsInline aria-hidden="true" className="absolute inset-0 h-full w-full object-cover object-[75%_center] motion-reduce:hidden" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/60 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-navy to-transparent" />
        <div className="absolute inset-0 bg-cover bg-center opacity-[0.07] mix-blend-screen" style={{ backgroundImage: `url(${chart})` }} />
        <div className="blueprint absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 md:py-36">
          <SdvosbBadge tone="dark" />
          <h1 className="mt-8 whitespace-pre-line font-display text-5xl uppercase leading-[0.92] md:text-8xl">
            {getCopy("home_hero_title", "Bold.\nVigilant.\nInnovative.")}
          </h1>
          <p className="mt-8 max-w-xl text-lg text-navy-foreground/85 md:text-xl">
            {getCopy("home_hero_description", "Rooted in Louisiana. Built for national impact. We help founders turn technology into companies — reaching customers across commercial, energy, and defense markets and preparing for investment.")}
          </p>
          <div className="mt-10 flex flex-col items-start gap-6">
            <Button asChild size="lg" className="h-12 rounded-none font-mono text-xs uppercase tracking-widest">
              <Link to="/intake">{getCopy("home_cta_button", "Accelerate Now")} <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Link to="/partners" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-navy-foreground transition-all hover:gap-3">{getCopy("home_hero_secondary_cta", "Explore Ecosystem")} <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-16 border-t border-navy-foreground/15 pt-6"><Framework tone="dark" /></div>
        </div>
      </section>

      {/* Pillars */}
      <section className="relative bg-surface bg-cover bg-center" style={{ backgroundImage: `linear-gradient(color-mix(in oklab, var(--surface) 70%, transparent), color-mix(in oklab, var(--surface) 70%, transparent)), url(${chart})` }}>
        <div className="mx-auto max-w-7xl px-6 py-24">
          <Eyebrow>Core Ecosystem Pillars</Eyebrow>
          <h2 className="mt-4 font-display text-4xl uppercase md:text-5xl">{getCopy("home_pillars_title", "How we accelerate the mission")}</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {pillars.map((p) => (
              <div key={p.title} className="rounded-lg border border-border bg-card p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="grid h-12 w-12 place-items-center rounded-md bg-primary text-primary-foreground"><p.icon className="h-6 w-6" /></div>
                  <span className="rounded-full bg-accent px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-primary">{p.badge}</span>
                </div>
                <h3 className="mt-6 font-display text-2xl uppercase">{p.title}</h3>
                <p className="mt-3 text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story imagery */}
      <section className="bg-navy text-navy-foreground">
        <div className="blueprint">
          <div className="mx-auto max-w-7xl px-6 py-24">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-navy-foreground/70">Louisiana roots · Regional reach · National impact</p>
            <h2 className="mt-4 max-w-3xl font-display text-4xl uppercase md:text-5xl">{getCopy("home_industries_title", "The industries we help founders build in")}</h2>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {story.map((st) => (
                <figure key={st.title} className="group overflow-hidden border border-navy-foreground/15 bg-steel">
                  <img src={st.img} alt={st.alt} width={1024} height={768} loading="lazy" className="aspect-[4/3] w-full object-cover transition duration-500 group-hover:scale-105" />
                  <figcaption className="p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-navy-foreground/60">{st.step}</p>
                    <h3 className="mt-2 font-display text-xl uppercase">{st.title}</h3>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who we serve */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <Eyebrow>Who We Serve</Eyebrow>
        <h2 className="mt-4 font-display text-4xl uppercase md:text-5xl">{getCopy("home_audiences_title", "Your gateway into the ecosystem")}</h2>
        <div className="mt-12 grid gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-3">
          {audiences.map((a) => (
            <Link key={a.title} to="/intake" className="group bg-card p-8 hover:bg-accent">
              <a.icon className="h-8 w-8 text-primary" />
              <h3 className="mt-6 font-display text-xl uppercase">{a.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground">{a.body}</p>
              <span className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary">Get started <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* Spotlights */}
      <section className="grid-bg border-y border-border">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <Eyebrow>Technology Spotlights</Eyebrow>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {spotlights.map((s) => (
              <div key={s.name} className="rounded-lg border border-border bg-card p-8 shadow-sm">
                <h3 className="font-display text-5xl text-primary">{s.name}</h3>
                <p className="mt-2 font-mono text-xs uppercase tracking-widest text-muted-foreground">{s.full}</p>
                <dl className="mt-6 divide-y divide-border border-y border-border">
                  {s.specs.map(([k, v]) => (
                    <div key={k} className="flex justify-between py-2 text-sm"><dt className="text-muted-foreground">{k}</dt><dd className="font-mono">{v}</dd></div>
                  ))}
                </dl>
                <div className="mt-6 flex flex-wrap gap-2">
                  {s.uses.map((u) => <span key={u} className="rounded-full bg-accent px-3 py-1 text-xs text-primary">{u}</span>)}
                </div>
                <Link to="/dual-use-accelerator" className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary">Full specifications <ArrowRight className="h-4 w-4" /></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-10 px-6 py-16 md:grid-cols-4">
          {stats.map(([n, l]) => (
            <div key={l}>
              <p className="font-display text-5xl">{n}</p>
              <p className="mt-2 font-mono text-xs uppercase tracking-widest opacity-80">{l}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 text-center">
        <h2 className="font-display text-4xl uppercase md:text-6xl">{getCopy("home_cta_title", "Ready to accelerate?")}</h2>
        <Link to="/intake" className="mt-8 inline-block bg-primary px-8 py-4 font-mono text-xs uppercase tracking-widest text-primary-foreground hover:bg-secondary">{getCopy("home_cta_button", "Accelerate Now")}</Link>
      </section>
    </Shell>
  );
}
